import { SerializedError, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import dayjs from 'dayjs'

export type AuditLogUserSummary = {
  id: number
  username: string
  email: string
  roleName: string | null
  userId?: number
}

export type RestoAuditLog = {
  id: number
  createdAt: string
  correlationId: string
  userId: number
  username?: string
  email?: string
  roleId?: number
  roleName?: string
  user?: AuditLogUserSummary | null
  subject: string
  action: string
  source: string
  requestedValue: null | any
  initialValue: null | any
  updatedValue: null | any
}

type FetchAuditLogParams = {
  pageSize: any
  userId: number | null
  dateFrom: Date | null
  dateTo: Date | null
  entitySource: string
  entitySourceId: number
  page: number
  includeDelegations?: boolean
}

type State = {
  auditLog: {
    loading: boolean
    data: RestoAuditLog[] | null
    count: number
    error: SerializedError | null
  }
  auditUsers: {
    loading: boolean
    data: AuditUser[]
    count: number
    error: SerializedError | null
  }
}

export type AuditUser = {
  id: number
  username: string
  email: string
  name: string
}

const initialState: State = {
  auditLog: {
    loading: false,
    data: [],
    count: 0,
    error: null
  },
  auditUsers: {
    loading: false,
    data: [],
    count: 0,
    error: null
  }
}

function marshalAuditLogParams(fetchAuditLogParams: FetchAuditLogParams) {
  const marshalledParams = {
    ...(fetchAuditLogParams.userId ? { userId: fetchAuditLogParams.userId.toString() } : {}),
    ...(fetchAuditLogParams.dateFrom
      ? { dateFrom: dayjs(fetchAuditLogParams.dateFrom).utc().startOf('D').toISOString() }
      : {}),
    ...(fetchAuditLogParams.dateTo ? { dateTo: dayjs(fetchAuditLogParams.dateTo).utc().endOf('D').toISOString() } : {}),
    page: (fetchAuditLogParams.page + 1).toString(),
    pageSize: fetchAuditLogParams.pageSize.toString(),
    entitySource: fetchAuditLogParams.entitySource,
    entitySourceId: fetchAuditLogParams.entitySourceId.toString(),
    ...(fetchAuditLogParams.includeDelegations ? { includeDelegations: 'true' } : {})
  } as Record<string, string>

  const urlParams = new URLSearchParams(marshalledParams)

  return urlParams
}

export const fetchAuditLog = createAsyncThunk<RestoAuditLog[] | null, FetchAuditLogParams, Redux>(
  'api/fetchAuditLog',
  async (fetchAuditLogParams, { extra }) => {
    try {
      if (!(fetchAuditLogParams.dateFrom && fetchAuditLogParams.dateTo)) {
        return
      }
      const urlParams = marshalAuditLogParams(fetchAuditLogParams)
      const response = await extra.httpClient(`resto365-audit?${urlParams.toString()}`)
      const {
        data: { data: entries }
      } = await response

      return entries
    } catch {
      throw new Error('Failed to fetchAuditLog')
    }
  }
)

export const fetchAuditLogCount = createAsyncThunk<number | null, FetchAuditLogParams, Redux>(
  'api/fetchAuditLogCount',
  async (fetchAuditLogParams, { extra }) => {
    try {
      if (!(fetchAuditLogParams.dateFrom && fetchAuditLogParams.dateTo)) {
        return
      }
      const urlParams = marshalAuditLogParams(fetchAuditLogParams)
      const response = await extra.httpClient(`resto365-audit/count?${urlParams.toString()}`)
      const {
        data: { data: count }
      } = await response

      return count
    } catch {
      throw new Error('Failed to fetchAuditLogCount')
    }
  }
)

export const fetchAuditUsers = createAsyncThunk<AuditUser[] | null, string, Redux>(
  'api/fetchAuditUsers',
  async (query, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-user?name=${query}`)
      const {
        data: { data: users }
      } = await response

      return users
    } catch {
      throw new Error('Failed to fetchAuditUsers')
    }
  }
)

const auditLogReducer = createSlice({
  name: 'auditLogReducer',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchAuditLog.pending, state => {
      state.auditLog.loading = true
    })
    builder.addCase(fetchAuditLog.fulfilled, (state, action) => {
      state.auditLog.loading = false
      state.auditLog.data = action.payload
    })
    builder.addCase(fetchAuditLog.rejected, (state, action) => {
      state.auditLog.loading = false
      state.auditLog.error = action.error
    })
    builder.addCase(fetchAuditLogCount.pending, state => {
      state.auditLog.loading = true
    })
    builder.addCase(fetchAuditLogCount.fulfilled, (state, action) => {
      state.auditLog.loading = false
      state.auditLog.count = action.payload || 0
    })
    builder.addCase(fetchAuditLogCount.rejected, (state, action) => {
      state.auditLog.loading = false
      state.auditLog.error = action.error
    })
    builder.addCase(fetchAuditUsers.pending, state => {
      state.auditUsers.loading = true
    })
    builder.addCase(fetchAuditUsers.fulfilled, (state, action) => {
      state.auditUsers.loading = false
      state.auditUsers.data = action.payload || []
    })
    builder.addCase(fetchAuditUsers.rejected, (state, action) => {
      state.auditUsers.loading = false
      state.auditUsers.error = action.error
    })
  }
})

export default auditLogReducer.reducer
